import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import isArray from 'lodash/fp/isArray'
import isPlainObject from 'lodash/fp/isPlainObject'
import isString from 'lodash/fp/isString'
import isEmpty from 'lodash/fp/isEmpty'
import getOr from 'lodash/fp/getOr'
import compose from 'lodash/fp/compose'
import NumberFormat from 'react-number-format'

import InputWrapper from './InputWrapper'
import InputBase from './InputBase'
import TextareaBase from './TextareaBase'
import InputAdornment from './InputAdornment';

const isControlled = (key, props) => props[key] !== undefined
const callIf = fn => (...args) => fn && fn(...args)
const checkFilled = value => {
  if (isArray(value) || isPlainObject(value) || isString(value)) {
    return !isEmpty(value)
  }
  return value != null
}
const isFilled = compose(checkFilled, getOr('', 'value'))

class Input extends PureComponent {
  static propTypes = {
    name: PropTypes.string.isRequired,
    id: PropTypes.string,
    type: PropTypes.string,
    prefix: PropTypes.node,
    suffix: PropTypes.node,
    full: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onEmpty: PropTypes.func,
    onFilled: PropTypes.func,
    onFocus: PropTypes.func
  }
  static defaultProps = {
    type: 'text'
  }

  static getDerivedStateFromProps(props, state) {
    // if disable the component but it still in focused state
    // we need to update focused state
    if (
      !isControlled('focused', props) &&
      props.disabled &&
      state.focused
    ) {
      return { focused: false }
    }
    return null
  }

  constructor(props) {
    super(props)

    if (isControlled('value', props)) {
      this.checkDirty(this.props)
    }
  }

  fieldRef = React.createRef()
  state = { focused: false }

  componentDidMount() {
    if (!isControlled('value', this.props)) {
      this.checkDirty(this.fieldRef.current)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      isControlled('value', this.props) &&
      prevProps.value !== this.props.value
    ) {
      this.checkDirty(this.props)
    }

    // if disable the component it dont trigger blur event
    // we need to trigger to update the control focused state
    if (this.getState().focused && this.props.disabled) {
      callIf(this.props.onBlur)()
    }
  }

  getState() {
    return Object.entries(this.state).reduce((state, [key, value]) => ({
      ...state,
      [key]: isControlled(key, this.props) ? this.props[key] : value
    }), {})
  }

  checkDirty(obj) {
    if (isFilled(obj)) {
      callIf(this.props.onFilled)()
      return
    }
    callIf(this.props.onEmpty)()
  }

  handleFocus = event => {
    if (this.props.disabled) {
      event.stopPropagation()
      return
    }

    if (!isControlled('focused', this.props)) {
      this.setState(
        state => !state.focused ? { focused: true } : null,
      )
    }
    callIf(this.props.onFocus)(event)
  }

  handleBlur = event => {
    if (!isControlled('focused', this.props)) {
      this.setState(
        state => state.focused ? { focused: false } : null
      )
    }

    callIf(this.props.onBlur)(event)
  }

  handleChange = event => {
    if (!isControlled('value', this.props)) {
      this.checkDirty(this.fieldRef.current)
    }

    callIf(this.props.onChange)(event)
  }

  render() {
    const {
      name,
      id = name,
      full,
      disabled,
      error,
      prefix,
      suffix,
      onFilled: ignoredOnFilled,
      onEmpty: ignoredOnEmpty,
      ...props
    } = this.props
    const { focused } = this.getState()
    const inputProps = {
      ...props,
      name,
      id,
      focused,
      error,
      disabled,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      onChange: this.handleChange
    }
    const input = props.type === 'textarea'
      ? <TextareaBase {...inputProps} />
      : props.type === 'numeric'
        ? (
          <NumberFormat
            {...inputProps}
            customInput={InputBase}
            type='text'
          />
        ) : <InputBase {...inputProps} />

    return (
      <InputWrapper
        focused={focused}
        error={error}
        full={full}
        disabled={disabled}
      >
        {prefix && <InputAdornment position='start'>{prefix}</InputAdornment>}
        {input}
        {suffix && <InputAdornment position='end'>{suffix}</InputAdornment>}
      </InputWrapper>
    )
  }
}

export default Input
